import { useState } from "react";
import { useNotify, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import type { QuoterModelListPrice, QuoterPriceList } from "../types";
import { useSaveQuoterConfig } from "./quoterAdminData";
import {
  hasPriceListErrors,
  parseNumber,
  priceListToDraft,
  validatePriceListDraft,
  type PriceListDraft,
  type PriceListErrors,
} from "./quoterConfigRules";
import {
  listPrice,
  listThresholds,
  type QuoterProjectData,
} from "./quoterEngine";
import { formatMxn } from "./quoterFormat";
import { QuoterField } from "./QuoterField";

const activeLists = (priceLists: QuoterPriceList[]) =>
  priceLists
    .filter((list) => !list.archived_at)
    .sort((a, b) => a.list_number - b.list_number);

/**
 * The lists as the draft would leave them: valid drafted blocks, increments and
 * captured prices replace the saved ones, so the preview reacts while typing.
 */
const withDrafts = (
  project: QuoterProjectData,
  drafts: Record<string, PriceListDraft>,
) => {
  const priceLists = project.priceLists.map((list) => {
    const draft = drafts[String(list.id)];
    if (!draft) return list;
    const block = parseNumber(draft.block_size);
    const pct = parseNumber(draft.increment_pct);
    return {
      ...list,
      block_size: block != null && block >= 1 ? block : list.block_size,
      increment_pct: pct != null && pct >= 0 ? pct : list.increment_pct,
    };
  });
  const captured: QuoterModelListPrice[] = Object.entries(drafts).flatMap(
    ([listId, draft]) =>
      Object.entries(draft.captured).flatMap(([modelId, raw]) => {
        const price = parseNumber(raw);
        const list = project.priceLists.find(
          (row) => String(row.id) === listId,
        );
        const model = project.models.find((row) => String(row.id) === modelId);
        if (price == null || price <= 0 || !list || !model) return [];
        return [
          {
            id: `${listId}-${modelId}`,
            price_list_id: list.id,
            model_id: model.id,
            price,
            created_at: "",
            updated_at: "",
          },
        ];
      }),
  );
  // A list without a draft keeps its saved captured prices.
  const untouched = project.listPrices.filter(
    (row) => drafts[String(row.price_list_id)] == null,
  );
  return { priceLists, listPrices: [...untouched, ...captured] };
};

/**
 * Almikia Epic EQ2 (TASK-099): the price lists of a project. Each list has its
 * own sales block and EITHER an increment % over the previous list OR a captured
 * price per model (a captured price wins). Below the table a read-only preview
 * shows the computed price of every model on every list, recomputed with the
 * same engine the quoter uses. A captured price can be changed but not
 * removed (nothing is ever hard deleted).
 */
export const PriceListsEditor = ({
  project,
}: {
  project: QuoterProjectData;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const save = useSaveQuoterConfig();
  const [drafts, setDrafts] = useState<Record<string, PriceListDraft>>({});
  const [errors, setErrors] = useState<Record<string, PriceListErrors>>({});
  const [busy, setBusy] = useState(false);

  const lists = activeLists(project.priceLists);
  const models = project.models
    .filter((model) => !model.archived_at)
    .sort((a, b) => a.sort_order - b.sort_order);
  const savedCaptured = (list: QuoterPriceList) =>
    Object.fromEntries(
      project.listPrices
        .filter((row) => row.price_list_id === list.id)
        .map((row) => [String(row.model_id), String(row.price)]),
    );
  const draftOf = (list: QuoterPriceList): PriceListDraft =>
    drafts[String(list.id)] ?? priceListToDraft(list, savedCaptured(list));
  const setDraft = (list: QuoterPriceList, patch: Partial<PriceListDraft>) =>
    setDrafts((current) => ({
      ...current,
      [String(list.id)]: { ...draftOf(list), ...patch },
    }));

  const effective = withDrafts(project, drafts);
  const thresholds = listThresholds(effective.priceLists);

  const saveList = async (list: QuoterPriceList) => {
    const draft = draftOf(list);
    const found = validatePriceListDraft(draft, list.list_number);
    const existing = project.listPrices.filter(
      (row) => row.price_list_id === list.id,
    );
    for (const row of existing) {
      const key = String(row.model_id);
      if ((draft.captured[key] ?? "").trim() === "") {
        found.captured[key] = "captured_locked";
      }
    }
    setErrors((current) => ({ ...current, [String(list.id)]: found }));
    if (hasPriceListErrors(found)) return;
    setBusy(true);
    try {
      await save("quoter_price_lists", list.id, {
        block_size: parseNumber(draft.block_size),
        increment_pct:
          list.list_number > 1 ? parseNumber(draft.increment_pct) : null,
      });
      for (const [modelId, raw] of Object.entries(draft.captured)) {
        const price = parseNumber(raw);
        if (price == null) continue;
        const row = existing.find((item) => String(item.model_id) === modelId);
        if (row?.price === price) continue;
        const model = models.find((item) => String(item.id) === modelId);
        if (!model) continue;
        await save(
          "quoter_model_list_prices",
          row?.id ?? null,
          row
            ? { price }
            : { price_list_id: list.id, model_id: model.id, price },
        );
      }
      setDrafts((current) =>
        Object.fromEntries(
          Object.entries(current).filter(([key]) => key !== String(list.id)),
        ),
      );
      notify("crm.quoter.config.saved");
    } catch {
      notify("crm.quoter.config.save_error", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const addList = async () => {
    const last = lists[lists.length - 1];
    setBusy(true);
    try {
      await save("quoter_price_lists", null, {
        project_id: project.config.project_id,
        list_number: (last?.list_number ?? 0) + 1,
        block_size: last?.block_size ?? 30,
        increment_pct: last ? (last.increment_pct ?? 0) : null,
      });
    } catch {
      notify("crm.quoter.config.save_error", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="space-y-4"
      aria-label={translate("crm.quoter.lists.title")}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">
          {translate("crm.quoter.lists.title")}
        </h2>
        <Button
          type="button"
          variant="outline"
          className="h-11 sm:h-9"
          disabled={busy}
          onClick={addList}
        >
          {translate("crm.quoter.lists.add")}
        </Button>
      </div>
      <p className="text-base text-muted-foreground sm:text-sm">
        {translate("crm.quoter.lists.intro")}
      </p>

      {lists.length === 0 ? (
        <p className="text-base text-muted-foreground sm:text-sm">
          {translate("crm.quoter.lists.empty")}
        </p>
      ) : (
        <ul className="space-y-3" data-testid="price-lists">
          {lists.map((list) => {
            const draft = draftOf(list);
            const rowErrors = errors[String(list.id)];
            const needed = thresholds.find(
              (item) => item.list_number === list.list_number,
            )?.sales_needed;
            return (
              <li key={list.id}>
                <Card>
                  <CardContent className="space-y-3 p-4">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h3 className="font-medium">
                        {translate("crm.quoter.lists.list_n", {
                          n: list.list_number,
                        })}
                      </h3>
                      <span className="text-base text-muted-foreground tabular-nums sm:text-sm">
                        {translate("crm.quoter.lists.reached_with", {
                          sales: needed ?? 0,
                        })}
                      </span>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <QuoterField
                        id={`price-list-${list.id}-block`}
                        label={translate("crm.quoter.lists.block_size")}
                        value={draft.block_size}
                        numeric
                        error={rowErrors?.block_size}
                        onChange={(value) =>
                          setDraft(list, { block_size: value })
                        }
                      />
                      {list.list_number > 1 ? (
                        <QuoterField
                          id={`price-list-${list.id}-increment`}
                          label={translate("crm.quoter.lists.increment_pct")}
                          value={draft.increment_pct}
                          numeric
                          error={rowErrors?.increment_pct}
                          onChange={(value) =>
                            setDraft(list, { increment_pct: value })
                          }
                        />
                      ) : null}
                    </div>
                    {list.list_number > 1 ? (
                      <fieldset className="space-y-2">
                        <legend className="text-base font-medium sm:text-sm">
                          {translate("crm.quoter.lists.captured")}
                        </legend>
                        <p className="text-sm text-muted-foreground">
                          {translate("crm.quoter.lists.captured_hint")}
                        </p>
                        <div className="grid gap-3 sm:grid-cols-3">
                          {models.map((model) => (
                            <QuoterField
                              key={model.id}
                              id={`price-list-${list.id}-model-${model.id}`}
                              label={model.name}
                              value={draft.captured[String(model.id)] ?? ""}
                              numeric
                              error={rowErrors?.captured[String(model.id)]}
                              onChange={(value) =>
                                setDraft(list, {
                                  captured: {
                                    ...draft.captured,
                                    [String(model.id)]: value,
                                  },
                                })
                              }
                            />
                          ))}
                        </div>
                      </fieldset>
                    ) : null}
                    <Button
                      type="button"
                      className="h-11 sm:h-9"
                      disabled={busy}
                      onClick={() => saveList(list)}
                    >
                      {translate("crm.quoter.lists.save_list", {
                        n: list.list_number,
                      })}
                    </Button>
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {lists.length > 0 && models.length > 0 ? (
        <Card>
          <CardContent className="space-y-2 p-4">
            <h3 className="font-medium">
              {translate("crm.quoter.lists.preview")}
            </h3>
            <div className="overflow-x-auto">
              <table
                className="w-full text-base tabular-nums sm:text-sm"
                data-testid="price-preview"
              >
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-1 pr-3 font-medium">
                      {translate("crm.quoter.lists.model")}
                    </th>
                    {lists.map((list) => (
                      <th key={list.id} className="py-1 pr-3 font-medium">
                        {translate("crm.quoter.lists.list_n", {
                          n: list.list_number,
                        })}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {models.map((model) => (
                    <tr key={model.id} className="border-t">
                      <th className="py-1 pr-3 text-left font-medium">
                        {model.name}
                      </th>
                      {lists.map((list) => (
                        <td key={list.id} className="py-1 pr-3">
                          {formatMxn(
                            listPrice(
                              model,
                              effective.priceLists,
                              effective.listPrices,
                              list.list_number,
                            ),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </section>
  );
};
